/**
 * Skeleton component for payroll lookup page loading state
 * @module components/common/PayrollLookupSkeleton
 */

import React from 'react'
import { Card } from '@/components/ui/Card'
import { SkeletonText, SkeletonRounded, SkeletonCircular } from './Skeleton'

/**
 * Payroll lookup loading skeleton
 *
 * Displays skeleton placeholders matching the structure of the payroll lookup page
 */
export const PayrollLookupSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Employee Info Card */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <SkeletonCircular width={48} height={48} />
          <div className="space-y-2">
            <SkeletonText width={200} height={24} />
            <div className="flex items-center gap-2">
              <SkeletonText width={180} height={16} />
              <SkeletonRounded width={60} height={20} />
              <SkeletonRounded width={50} height={20} />
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <SkeletonText width={80} height={14} />
                <SkeletonText width={120} height={28} />
              </div>
              <SkeletonRounded width={36} height={36} />
            </div>
          </Card>
        ))}
      </div>

      {/* Current Payroll Breakdown */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <SkeletonCircular width={20} height={20} />
          <SkeletonText width={280} height={20} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Earnings */}
          <div>
            <SkeletonText width={80} height={14} className="mb-3" />
            <div className="bg-brand-dark-lighter rounded-lg p-4 space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <SkeletonCircular width={16} height={16} />
                    <SkeletonText width={100} height={14} />
                  </div>
                  <SkeletonText width={80} height={14} />
                </div>
              ))}
            </div>
          </div>

          {/* Deductions */}
          <div>
            <SkeletonText width={80} height={14} className="mb-3" />
            <div className="bg-brand-dark-lighter rounded-lg p-4 space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <SkeletonCircular width={16} height={16} />
                    <SkeletonText width={100} height={14} />
                  </div>
                  <SkeletonText width={80} height={14} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="mt-6 pt-4 border-t border-brand-dark-lighter flex justify-between items-center">
          <SkeletonText width={120} height={20} />
          <SkeletonText width={150} height={28} />
        </div>
      </Card>

      {/* Tabs skeleton */}
      <div className="flex gap-2">
        <SkeletonRounded width={180} height={36} />
        <SkeletonRounded width={120} height={36} />
      </div>

      {/* History table */}
      <Card className="p-6">
        <SkeletonText width={200} height={20} className="mb-4" />
        <div className="space-y-3">
          {/* Header */}
          <div className="grid grid-cols-5 gap-4 py-3 border-b border-brand-dark-lighter">
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonText key={index} width="80%" height={14} />
            ))}
          </div>
          {/* Rows */}
          {Array.from({ length: 6 }).map((_, rowIndex) => (
            <div key={rowIndex} className="grid grid-cols-5 gap-4 py-3">
              {Array.from({ length: 5 }).map((_, colIndex) => (
                <SkeletonText key={colIndex} width="70%" height={14} />
              ))}
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
